<template>
  <div>
    <b-container class="my-2">
      <b-row>
        <b-col>
          <b-collapse
            class="pt-2"
            :visible="status.showHelp"
            id="collapse-help"
          >
            <b-alert show variant="warning">
              <div>
                <h4 class="font-weight-bold">
                  <!-- <b-icon icon="question-circle"></b-icon>  -->
                  📖️ Annotating Documents
                </h4>
                <!-- <p class="font-weight-bold">Let's get started!</p> -->
                <hr />
                ECCE has automatically suggested named entity annotations, which
                are highlighted in your documents. Click “Edit” to open a
                document. You have the option to correct all annotations by
                adding and deleting them, or by choosing a different entity
                type.
                <b-link v-b-toggle.collapse-help-entity-types variant="primary"
                  >Click here <b-icon font-scale="1.0" icon="link45deg"></b-icon
                ></b-link>
                for a list of entity types used. Advance by clicking on
                <b-link @click="goToExploreGraph()" variant="primary"
                  >Extract Graph
                  <b-icon font-scale="1.0" icon="link45deg"></b-icon></b-link
                >.

                <b-collapse id="collapse-help-entity-types" class="mt-2">
                  <b-card>
                    <b-table
                      striped
                      small
                      :items="entityTypeExplained"
                    ></b-table>

                    <p class="text-muted">
                      The explanations originate from the documentation of the
                      <a
                        href="https://catalog.ldc.upenn.edu/docs/LDC2013T19/"
                        target="blank"
                        >OneNote dataset</a
                      >.
                    </p>
                  </b-card>
                </b-collapse>
              </div>
              <b-button class="my-2" variant="primary" @click="closeHelp()"
                >Close Help</b-button
              >
            </b-alert>
          </b-collapse>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="5" class="py-2 my-auto">
          <b-button
            size="md"
            v-b-tooltip.hover
            title="Annotate all documents."
            id="popover-reparse-button"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="gear"></b-icon>
            Annotate Documents
          </b-button>

          <!-- Reparsing Menue -->
          <b-popover
            size="sm"
            id="popover-reparse-content"
            target="popover-reparse-button"
            triggers="click"
            :show.sync="menues.parsing.popoverShow"
            placement="bottom"
            container="popover-reparse-button-content"
            ref="popover"
          >
            <template #title>
              <b-button @click="onClose" class="close" aria-label="Close">
                <span class="d-inline-block" aria-hidden="true">&times;</span>
              </b-button>
              Parsing Options
            </template>

            <div>
              <!-- <b-form-group
                label=""
                label-for="popover-reparse-input-entity-types"
              >
                <b-link cv-b-tooltip.hover title="Tooltip content"
                  >Entity Types Explained
                </b-link>
              </b-form-group> -->

              <!-- Entity Types -->
              <b-form-group
                label="Choose entity types to identify in the corpus:"
                label-for="popover-reparse-input-entity-types"
              >
                <b-form-tags
                  id="popover-reparse-input-entity-types"
                  v-model="menues.parsing.options.typesSelected"
                  no-outer-focus
                  class="mb-2"
                >
                  <template v-slot="{ tags, disabled, addTag, removeTag }">
                    <ul
                      v-if="tags.length > 0"
                      class="list-inline d-inline-block mb-2"
                    >
                      <li
                        v-for="tag in tags"
                        :key="tag"
                        class="list-inline-item"
                      >
                        <b-form-tag
                          @remove="removeTag(tag)"
                          :title="tag"
                          :disabled="disabled"
                          variant="secondary"
                          >{{ tag }}</b-form-tag
                        >
                      </li>
                    </ul>

                    <b-dropdown
                      size="sm"
                      variant="outline-secondary"
                      block
                      menu-class="w-100"
                    >
                      <template #button-content> Add Entity Type </template>
                      <b-dropdown-item-button
                        v-for="option in availableOptions"
                        :key="option"
                        @click="onOptionClick({ option, addTag })"
                      >
                        {{ option }}
                      </b-dropdown-item-button>
                      <b-dropdown-text v-if="availableOptions.length === 0">
                        There are no further entity types available to select.
                      </b-dropdown-text>
                    </b-dropdown>
                  </template>
                </b-form-tags>
              </b-form-group>

              <b-button @click="onClose" variant="danger">Cancel</b-button>
              <b-button @click="onOk" variant="primary"
                >Annotate Documents</b-button
              >
            </div>
          </b-popover>

          <b-button
            size="md"
            v-b-tooltip.hover
            title="Refresh the document list."
            v-on:click="updateDocumentList(corpus_id)"
            :disabled="!validateCorpusId(corpus_id)"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="arrow-repeat"></b-icon>
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="md"
            title="Help"
            @click="toogleHelp()"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="question-circle"></b-icon>
          </b-button>
        </b-col>
        <b-col md="3" class="py-2 my-auto">
          <h5 class="text-muted my-1" align="left">
            Documents:
            <b-badge variant="primary">{{ pagination.documents }}</b-badge>
          </h5>
        </b-col>
        <b-col xl="4" class="py-2 my-auto">
          <b-button
            size="md"
            v-on:click="goToManageDocuments()"
            v-b-tooltip.hover
            title="Return to the document list."
            variant="primary"
            class="shadow my-1"
          >
            <b-icon icon="chevron-compact-left"></b-icon>
            Documents
          </b-button>
          <b-button
            size="md"
            v-on:click="goToExploreGraph()"
            v-b-tooltip.hover
            title="Construct the entity network."
            variant="primary"
            class="shadow my-1"
          >
            Extract Graph
            <b-icon icon="chevron-compact-right"></b-icon>
          </b-button>
        </b-col>
      </b-row>

      <!-- Conditional Rendering of Document List-->
      <b-overlay
        :show="status.showSpinner"
        rounded="sm"
        style="min-height: 400px"
      >
        <b-card-group deck>
          <b-card
            class="my-2"
            v-for="(document, id) in documents"
            :key="id"
            style="min-width: 350px"
          >
            <p
              class="card-text"
              style="min-height: 400px; max-height: 400px; overflow: hidden"
            >
              <span
                v-for="(token, id) in document.text_parsed.slice(0, 180)"
                :key="id"
              >
                <span v-if="token.is_entity">
                  <span v-if="token.ent_iob === 'B'">
                    <span
                      class="badge badge-secondary"
                      :style="{
                        'background-color': entityColorScale(token.entity_type),
                      }"
                    >
                      {{ token.entity_type }}:
                    </span>
                    {{ token.text }}
                    <span> </span>
                  </span>
                  <span v-else>
                    {{ token.text }}
                    <span> </span>
                  </span>
                </span>
                <span v-else class="text-muted">{{ token.text }} </span>
              </span>
            </p>
            <div slot="footer">
              <b-link variant="primary" v-on:click="openModalAnnotate(id)" block
                >Edit</b-link
              >
              <span class="text-muted"> - {{ document._id["$oid"] }}</span>
            </div>
          </b-card>
        </b-card-group>
        <!-- use shared component to add a b-row containing pagination -->
        <pagination :pagination="pagination"></pagination>
      </b-overlay>
    </b-container>

    <!-- Modals -->
    <b-modal
      id="modal-annotate"
      ref="modalAnnotate"
      title="Annotate Document"
      size="xl"
      scrollable
      no-fade
      @ok="editDocumentParsed()"
    >
      <b-container
        style="min-height: 500px"
        class="my-4"
        ref="modalAnnotateText"
      >
        <b-row align-h="center">
          <b-col cols="11">
            <span
              v-for="(token, id) in modals.annotate.text.slice(
                0,
                modals.annotate.tokensShown
              )"
              :key="id"
              style="margin-right: 3px"
            >
              <!-- Entity -->
              <a
                v-if="token.is_entity"
                v-bind:id="'token-' + token.s_id + '-' + token.t_id"
                href="#"
                v-on:click.prevent="onOpenToken($event, token, id)"
              >
                <span v-if="token.ent_iob === 'B'">
                  <!-- Set badge color dynamicly -->
                  <span
                    class="badge badge-primary"
                    :style="{
                      'background-color': entityColorScale(token.entity_type),
                    }"
                  >
                    {{ token.entity_type }}:
                  </span>
                  {{ token.text }}
                </span>
                <span v-else>
                  {{ token.text }}
                </span>
              </a>

              <!-- no Entity -->
              <a
                v-else
                v-bind:id="'token-' + token.s_id + '-' + token.t_id"
                class="text-muted"
                href="#"
                v-on:click.prevent="onOpenToken($event, token, id)"
                >{{ token.text }}
              </a>
            </span>

            <span
              v-show="modals.annotate.tokensShown < modals.annotate.text.length"
            >
              ...</span
            >

            <!-- Token details tooltip -->
            <b-card
              id="tooltip-token-details"
              ref="tooltipTokenDetails"
              style="
                width: 320px;
                opacity: 1;
                position: absolute;
                left: 150px;
                top: 150px;
                z-index: -1;
              "
              class="mb-2 shadow bg-white"
            >
              <b-card-title>
                <b-button
                  id="tooltip-token-button-close"
                  class="close"
                  @click="onCloseToken"
                >
                  <span class="d-inline-block">&times;</span>
                </b-button>
                <span class="text-muted"
                  >Token: "{{ menues.annotate.token.text }}"
                </span>
                <hr
              /></b-card-title>
              <b-card-text>
                <!-- Entity type -->
                <b-form-group
                  label="Entity Type"
                  label-for="popover-token-entity-typ"
                  label-cols="5"
                  class="mb-1"
                  description="Pick an entity type."
                  invalid-feedback="This field is required"
                >
                  <b-form-select
                    id="popover-token-entity-type"
                    v-model="menues.annotate.token.entity_type"
                    :options="menues.annotate.options.types"
                    v-on:change="
                      onChangeType(
                        menues.annotate.token,
                        menues.annotate.tokenId
                      )
                    "
                    size="sm"
                  ></b-form-select>
                </b-form-group>

                <!-- Compound entity -->
                <b-form-group
                  label="Comp. Entity"
                  label-for="popover-token-compound-entity"
                  label-cols="5"
                  class="mb-1"
                  description="Handle compound entities."
                >
                  <b-button
                    id="popover-token-compound-entity"
                    size="sm"
                    variant="outline-primary"
                    :disabled="tokenDisableAttach"
                    @click="
                      onAttachToEntity(
                        menues.annotate.token,
                        menues.annotate.tokenId
                      )
                    "
                    >Attach</b-button
                  >
                  <span class="text-muted"> or </span>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="onSplitEntity(menues.annotate.token)"
                    :disabled="menues.annotate.token.ent_iob != 'I'"
                    >Split</b-button
                  >
                </b-form-group>
                <hr />
                <!-- Is stopword -->
                <b-form-group
                  label=""
                  label-for="popover-token-is-stopword"
                  label-cols="5"
                  class="mb-1"
                >
                  <b-form-checkbox
                    id="popover-token-is-stopword"
                    v-model="menues.annotate.token.is_stopword"
                    :disabled="menues.annotate.token.entity_type != ''"
                    size="sm"
                  >
                    Is Stop Word
                  </b-form-checkbox>
                </b-form-group>
              </b-card-text>
            </b-card>
          </b-col>
        </b-row>
        <b-row
          align-h="center"
          v-show="modals.annotate.tokensShown < modals.annotate.text.length"
        >
          <b-col sm="auto">
            <b-button
              size="sm"
              class="my-4"
              @click="
                modals.annotate.tokensShown = modals.annotate.tokensShown + 1000
              "
              >load more</b-button
            >
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
  </div>
</template>

<script>
import pagination from "./shared/pagination.vue";
import requestsMixin from "../mixins/requestsMixin";
import messagesMixin from "../mixins/messagesMixin";
import constantsMixin from "../mixins/constantsMixin";

export default {
  name: "component-extract-entities",
  components: { pagination },
  mixins: [requestsMixin, messagesMixin, constantsMixin],
  props: {
    corpus_id: { type: String },
    reparseDocuments: { type: Boolean },
  },
  data() {
    return {
      entityTypeExplained: [
        { Type: "PERSON", Description: "People, including fictional" },
        {
          Type: "NORP",
          Description: "Nationalities or religious or political groups",
        },
        {
          Type: "FACILITY",
          Description: "Buildings, airports, highways, bridges, etc.",
        },
        {
          Type: "ORGANIZATION",
          Description: "Companies, agencies, institutions, etc.",
        },
        {
          Type: "GPE",
          Description: "Countries, cities, states",
        },
        {
          Type: "LOCATION",
          Description: "Non-GPE locations, mountain ranges, bodies of water",
        },
        {
          Type: "PRODUCT",
          Description: "Vehicles, weapons, foods, etc. (Not services)",
        },
        ,
        {
          Type: "EVENT",
          Description: "Named hurricanes, battles, wars, sports events, etc.",
        },
        {
          Type: "WORK OF ART",
          Description: "Titles of books, songs, etc.",
        },
        {
          Type: "LAW",
          Description: "Named documents made into laws",
        },
        {
          Type: "LANGUAGE",
          Description: "Any named language",
        },
        {
          Type: "DATE",
          Description: "Absolute or relative dates or periods",
        },
        {
          Type: "TIME",
          Description: "Times smaller than a day",
        },
        {
          Type: "PERCENT",
          Description: "Percentage (including “%”)",
        },
        {
          Type: "PERCENT",
          Description: "Monetary values, including unit",
        },
        {
          Type: "QUANTITY",
          Description: "Measurements, as of weight or distance",
        },
        {
          Type: "ORDINAL",
          Description: "'first', 'second'",
        },
        {
          Type: "CARDINAL",
          Description: "Numerals that do not fall under another type",
        },
      ],
      documents: [],
      menues: {
        // Variables used for
        // the parsing menue
        parsing: {
          options: {
            typesSelected: [
              "ORG",
              "GPE",
              "PERSON",
              "WORK_OF_ART",
              "NORP",
              "LOC",
            ],
            types: [],
          },
          popoverShow: false,
        },

        // Variables used for
        // the token details menue
        annotate: {
          token: {
            d_id: -1,
            s_id: -1,
            text: "",
            entity_type: false,
            ent_iob: "O",
          },
          tokenId: -1,
          options: {
            partOfEntity: [],
            types: [],
          },
        },
      },

      // Variables used to controle
      // pagination element
      pagination: {
        currentPage: 1,
        documents: 0,
        perPage: 30,
      },

      // Variables needed for
      // modals used to
      // annotate documents
      modals: {
        annotate: {
          text: [],
          tokensShown: 1000,
          localId: -1,
          documentId: "",
        },
      },

      // Variables used for tracking
      // the state of the component
      status: {
        showSpinner: false,
        showHelp: false,
      },
    };
  },
  methods: {
    /* ----------------------- Navigating the Application ----------------------- */

    // Functions for navigating
    goToExploreGraph() {
      this.$router.push({
        name: "explore_graph",
      });
    },

    goToManageDocuments() {
      this.$router.push({
        name: "manage_documents",
      });
    },

    /* ----------------------------- Reparse Corpus ----------------------------- */

    // Methods used to controle
    // the reparse menue
    onClose() {
      this.menues.parsing.popoverShow = false;
    },
    onOk() {
      this.parseCorpus(this.corpus_id, true);
      this.menues.parsing.popoverShow = false;
    },

    /* ---------------------------- Annotate Document --------------------------- */

    // Show modal for annotating text of a document
    // and store index, document_id and the text
    // in temp variables as long as modal open
    openModalAnnotate(id) {
      let modal = this.modals.annotate;
      modal.localId = id;
      modal.documentId = this.documents[id]._id.$oid;
      modal.text = this.documents[id].text_parsed;
      this.$refs.modalAnnotate.show();
      modal.tokensShown = 1000;
    },

    // Methods used to controle
    // the token details menue
    onChangeType(token, id) {
      let text = this.modals.annotate.text;
      // If token is marked as no entity ...
      if (token.entity_type == "") {
        token.is_entity = false;
        token.ent_iob = "O";
        id = id + 1;

        // Also mark all subsequent tokens as non entities
        while (text[id].ent_iob == "I") {
          text[id].ent_iob == "O";
          text[id].is_entity = false;
          text[id].entity_type = "";
          id = id + 1;
        }
      } else {
        // If token is marked as entity ...
        token.is_entity = true;
        token.is_stopword = false;

        if (token.ent_iob != "") {
          token.ent_iob = "B";
        }

        id = id + 1;
        // Also change type of all subsequent entity tokens
        while (text[id].ent_iob == "I") {
          text[id].entity_type = token.entity_type;
          id = id + 1;
        }
      }
    },

    onSplitEntity(token) {
      token.ent_iob = "B";
    },

    onAttachToEntity(token, id) {
      token.ent_iob = "I";
      token.is_stopword = false;
      token.is_entity = true;
      token.entity_type = this.modals.annotate.text[id - 1].entity_type;
    },

    onCloseToken() {
      const tooltip = this.$refs.tooltipTokenDetails;
      tooltip.style.zIndex = -1;
      tooltip.style.opacity = 0;
    },
    onOpenToken(ev, token, id) {
      // Load clicked token
      this.menues.annotate.token = token;
      this.menues.annotate.tokenId = id;

      // Reference objects
      const tooltip = this.$refs.tooltipTokenDetails;
      const modalText = this.$refs.modalAnnotateText;

      // Set position of popover
      tooltip.style.zIndex = 99999;
      tooltip.style.opacity = 1;
      const offsetTop = ev.target.offsetTop;
      const offsetLeft = ev.target.offsetLeft;
      const width = ev.target.offsetWidth;
      const height = ev.target.offsetHeight;
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const modalWidth = modalText.offsetWidth;
      const modalHeight = modalText.offsetHeight;

      if (offsetLeft + tooltipWidth + width / 2 <= modalWidth) {
        if (offsetTop + tooltipHeight <= modalHeight) {
          tooltip.style.top = offsetTop + height + "px";
          tooltip.style.left = offsetLeft + width / 2 + "px";
        } else {
          tooltip.style.top = offsetTop - tooltipHeight + "px";
          tooltip.style.left = offsetLeft + width / 2 + "px";
        }
      } else {
        if (offsetTop + tooltipHeight <= modalHeight) {
          tooltip.style.top = offsetTop + height + "px";
          tooltip.style.left = offsetLeft - tooltipWidth + width / 2 + "px";
        } else {
          tooltip.style.top = offsetTop - tooltipHeight + "px";
          tooltip.style.left = offsetLeft - tooltipWidth + width / 2 + "px";
        }
      }
    },

    // Add selected type to list of selected
    // types to search for in corpus
    onOptionClick({ option, addTag }) {
      addTag(option);
    },

    // Edit document and refresh document list
    editDocumentParsed() {
      new Promise((resolve) => {
        this.status.showSpinner = true; // Show spinner
        resolve(true);
      })
        .then(() => {
          return this.editDocumentParsedRemote(
            this.corpus_id,
            this.modals.annotate.documentId,
            this.modals.annotate.text
          );
        })
        .then(() => {
          this.modals.annotate.text = "";
          this.$refs.modalAnnotate.hide();
        })
        .then(() => {
          this.showToast(
            "Document updated",
            "Document annotations were successfully updated.",
            "success"
          );
        })
        // Stop spinner
        .finally(() => {
          this.status.showSpinner = false; // Hide spinner
        })
        // Shwow error message
        .catch((error) => {
          console.log(error);
          this.showToast(String(error.message), String(error.cause), "danger");
        });
    },

    /* -------------------------- Refresh Document List ------------------------- */
    // Fetch document subset according to the selected page.

    updateDocumentList(corpus_id) {
      if (!!corpus_id) {
        // Start spinner
        new Promise((resolve) => {
          this.status.showSpinner = true; // Show spinner
          resolve(true);
        })
          // Fetch meta information about corpus
          .then(() => {
            this.fetchCorpusMetaData(corpus_id).then((result_data) => {
              this.pagination.documents = result_data;
            });
          })
          // Fetch documents in corpus
          .then(() => {
            return this.fetchCorpus(
              corpus_id,
              this.pagination.currentPage,
              this.pagination.perPage
            );
          })
          .then((response) => {
            this.documents = response;
          })
          // Stop spinner
          .finally(() => {
            this.status.showSpinner = false; // Hide spinner
          })
          // Shwow error message
          .catch((error) => {
            console.log(error);
            this.showToast(
              String(error.message),
              String(error.cause),
              "danger"
            );
          });
      }
    },

    /* ------------------------ Parse Documents in Corpus ----------------------- */
    // Parse all documents in corpus and refresh document list.

    parseCorpus(corpus_id, forceParsing) {
      if (!!corpus_id) {
        // Start spinner
        new Promise((resolve) => {
          this.status.showSpinner = true; // Show spinner
          resolve(true);
        })
          .then(() => {
            return this.parseDocuments(
              corpus_id,
              forceParsing,
              this.menues.parsing.options.typesSelected
            );
          })
          // Fetch meta information about corpus
          .then(() => {
            return this.fetchCorpusMetaData(corpus_id);
          })
          .then((document_count) => {
            this.pagination.documents = document_count;
          })
          // Fetch documents in corpus
          .then(() => {
            return this.fetchCorpus(
              corpus_id,
              this.pagination.currentPage,
              this.pagination.perPage
            );
          })
          .then((response) => {
            return new Promise((resolve) => {
              this.documents = response;
              resolve(true);
            });
          })
          .then(() => {
            this.showToast(
              "Parsing complete",
              "All documents in corpus were successfully parsed.",
              "success"
            );
          })
          // Stop spinner
          .finally(() => {
            this.status.showSpinner = false; // Hide spinner
          })
          // Shwow error message
          .catch((error) => {
            console.log(error);
            this.showToast(
              String(error.message),
              String(error.cause),
              "danger"
            );
          });
      }
    },

    /* ---------------------------------- Help ---------------------------------- */

    toogleHelp() {
      this.status.showHelp = !this.status.showHelp;
      this.$cookies.set(
        "extractEntities.showHelp",
        this.status.showHelp === true ? 1 : 0,
        Infinity
      );
    },

    openHelp() {
      this.status.showHelp = true;
      this.$cookies.set("extractEntities.showHelp", 1, Infinity);
    },

    closeHelp() {
      this.status.showHelp = false;
      this.$cookies.set("extractEntities.showHelp", 0, Infinity);
    },
  },

  watch: {
    corpus_id(value) {
      this.updateDocumentList(value);
    },
    "pagination.currentPage"(value) {
      this.updateDocumentList(this.corpus_id);
    },
  },
  computed: {
    // Compute available types for parsing menue
    // by filtering out already selected options
    availableOptions() {
      const types = this.menues.parsing.options.types;
      const typesSelected = this.menues.parsing.options.typesSelected;
      const typesAvailable = types.filter(
        (type) => typesSelected.indexOf(type) === -1
      );
      return typesAvailable;
    },
    tokenDisableAttach: function () {
      if (this.modals.annotate.text.length === 0) {
        return true;
      } else {
        return (
          this.menues.annotate.token.is_entity ||
          !this.modals.annotate.text[
            Math.max(0, this.menues.annotate.tokenId - 1)
          ]["is_entity"] ||
          this.menues.annotate.tokenId == 0
        );
      }
    },
  },
  created() {
    if (this.reparseDocuments) {
      this.parseCorpus(this.corpus_id, false);
    } else {
      this.updateDocumentList(this.corpus_id);
    }

    // Init const values
    this.menues.parsing.options.types = this.const.entityTypes;
    this.menues.annotate.options.types = this.const.entityTypes;
    this.menues.annotate.options.partOfEntity = this.const.entityParts;

    /* ---------------------- Determine help window status ---------------------- */

    // Get previous state of the help window
    var showHelpPrevious = this.$cookies.get("extractEntities.showHelp");

    // Previous help window status is "open"
    if (showHelpPrevious == 1) {
      this.openHelp();
    }

    // No cooky set (first visit or disabled);
    // If the corpus_id cooky is not set at this point,
    // it is assumed that cookies are disabled.
    if (
      showHelpPrevious === null &&
      !(this.$cookies.get("corpus_id") === null)
    ) {
      this.openHelp();
    }
  },
};
</script>

<style scoped>
#popover-reparse-content {
  min-width: 350px;
}
</style>


// WEBPACK FOOTER //
// src/components/extractEntities.vue